import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
  type DocumentData,
} from "firebase/firestore";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { db, storage } from "../config/firebase";
import type {
  ChainSegment,
  FeedItem,
  HubChain,
  HubComment,
  HubMap,
  HubProfile,
  HubUser,
  ReactiveWorldsMapPackage,
  TargetType,
} from "../types";
import { titleOf } from "../utils/mapPackage";
import { runtimePreviewBlob } from "../runtime/runtimePreview";
import { mapRevisionHash } from "../utils/mapRevision";
import type { ProjectVerification } from "../projects/projectRepository";
import { publishVerifiedMapDocument } from "./creatorVerificationRepository";
import * as community from "./communityRepository";
import * as content from "./contentRepository";

export async function removeMap(u: HubUser, id: string) {
  if (!db) {
    const x = load(),
      m = x.maps.find((v) => v.id === id);
    if (!m || m.authorId !== u.uid)
      throw new Error("Only the creator can remove this maze.");
    m.isRemoved = true;
    m.isPublic = false;
    m.removedAt = new Date();
    m.updatedAt = new Date();
    save(x);
    return;
  }
  await content.removeMap(id);
}

const C = {
    users: "users",
    maps: "rw_maps",
    chains: "rw_chains",
    comments: "rw_comments",
    likes: "rw_likes",
  },
  KEY = "rw_hub_local_db_v1";
type Local = {
  maps: HubMap[];
  chains: HubChain[];
  comments: HubComment[];
  likes: string[];
  ratings: Record<string, number>;
  profiles: HubProfile[];
};
const blank = (): Local => ({
  maps: [],
  chains: [],
  comments: [],
  likes: [],
  ratings: {},
  profiles: [],
});
const uid = (p: string) =>
  `${p}_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
function load(): Local {
  try {
    const x = JSON.parse(localStorage.getItem(KEY) || "") as Local;
    if (!x || !Array.isArray(x.maps)) return blank();
    x.ratings = x.ratings && typeof x.ratings === "object" ? x.ratings : {};
    return x;
  } catch {
    return blank();
  }
}
function save(x: Local) {
  localStorage.setItem(KEY, JSON.stringify(x));
}
const date = (v: unknown) =>
  v &&
  typeof v === "object" &&
  "toDate" in v &&
  typeof (v as { toDate?: unknown }).toDate === "function"
    ? (v as { toDate: () => Date }).toDate()
    : v instanceof Date
      ? v
      : new Date();
const mazeTitle = (value: unknown) => {
  const title = String(value || "Untitled Maze");
  return title.trim().toLowerCase() === "untitled map" ? "Untitled Maze" : title;
};
const joinedMazeTitle = (value: unknown) => {
  const title = String(value || "Untitled Joined Maze"), normalized = title.trim().toLowerCase();
  return ["untitled world", "untitled joined world"].includes(normalized) ? "Untitled Joined Maze" : title;
};
const reviveMap = (m: HubMap): HubMap => ({
  ...m,
  title: mazeTitle(m.title),
  verified: m.verified === true,
  uniqueAttemptCount: Math.max(0, Number(m.uniqueAttemptCount) || 0),
  uniqueFailureCount: Math.max(0, Number(m.uniqueFailureCount) || 0),
  escapeCount: Math.max(0, Number(m.escapeCount) || 0),
  abandonCount: Math.max(0, Number(m.abandonCount) || 0),
  rematchCount: Math.max(0, Number(m.rematchCount) || 0),
  bountyGold: Math.max(25, Number(m.bountyGold) || 25),
  creatorEarningsGold: Math.max(0, Number(m.creatorEarningsGold) || 0),
  lineageRoyaltyGold: Math.max(0, Number(m.lineageRoyaltyGold) || 0),
  saveCount: Math.max(0, Number(m.saveCount) || 0),
  ratingSum: Math.max(0, Number(m.ratingSum) || 0),
  ratingCount: Math.max(0, Number(m.ratingCount) || 0),
  averageRating:
    Math.max(0, Number(m.ratingCount) || 0) > 0
      ? Math.max(0, Number(m.ratingSum) || 0) /
        Math.max(1, Number(m.ratingCount) || 0)
      : 0,
  createdAt: new Date(m.createdAt),
  updatedAt: new Date(m.updatedAt),
});
const reviveChain = (c: HubChain): HubChain => ({
  ...c,
  title: joinedMazeTitle(c.title),
  segments: c.segments.map((segment) => ({ ...segment, title: mazeTitle(segment.title) })),
  createdAt: new Date(c.createdAt),
  updatedAt: new Date(c.updatedAt),
});
const reviveComment = (c: HubComment): HubComment => ({
  ...c,
  createdAt: new Date(c.createdAt),
  updatedAt: new Date(c.updatedAt),
});
function mapDoc(id: string, d: DocumentData): HubMap {
  let inlinePackage = d.package;
  try {
    if (typeof d.packageJson === "string")
      inlinePackage = JSON.parse(d.packageJson);
  } catch {
    inlinePackage = undefined;
  }
  const ratingSum = Math.max(0, Number(d.ratingSum) || 0),
    ratingCount = Math.max(0, Number(d.ratingCount) || 0);
  return {
    id,
    authorId: d.authorId,
    authorName: d.authorName,
    title: mazeTitle(d.title),
    description: d.description || "",
    theme: d.theme || "depot",
    levelType: d.levelType || "Puzzle",
    physicsVersion: d.physicsVersion || "unknown",
    isPublic: d.isPublic !== false,
    tags: d.tags || [],
    package: inlinePackage,
    packageUrl: d.packageUrl,
    thumbnailUrl: d.thumbnailUrl,
    createdAt: date(d.createdAt),
    updatedAt: date(d.updatedAt),
    likeCount: d.likeCount || 0,
    ratingSum,
    ratingCount,
    averageRating: ratingCount ? ratingSum / ratingCount : 0,
    commentCount: d.commentCount || 0,
    playCount: d.playCount || 0,
    remixCount: d.remixCount || 0,
    remixOf: d.remixOf || undefined,
    rootMapId: d.rootMapId || undefined,
    lineage: d.lineage || [],
    verified: d.verified === true,
    revisionHash: d.revisionHash || undefined,
    creatorVerifiedAt: d.creatorVerifiedAt ? date(d.creatorVerifiedAt) : undefined,
    uniqueAttemptCount: Math.max(0, Number(d.uniqueAttemptCount) || 0),
    uniqueFailureCount: Math.max(0, Number(d.uniqueFailureCount) || 0),
    escapeCount: Math.max(0, Number(d.escapeCount) || 0),
    abandonCount: Math.max(0, Number(d.abandonCount) || 0),
    rematchCount: Math.max(0, Number(d.rematchCount) || 0),
    bountyGold: Math.max(25, Number(d.bountyGold) || 25),
    creatorEarningsGold: Math.max(0, Number(d.creatorEarningsGold) || 0),
    lineageRoyaltyGold: Math.max(0, Number(d.lineageRoyaltyGold) || 0),
    saveCount: Math.max(0, Number(d.saveCount) || 0),
    isRemoved: d.isRemoved === true,
    removedAt: d.removedAt ? date(d.removedAt) : undefined,
  };
}
function chainDoc(id: string, d: DocumentData): HubChain {
  return {
    id,
    authorId: d.authorId,
    authorName: d.authorName,
    title: joinedMazeTitle(d.title),
    description: d.description || "",
    isPublic: d.isPublic !== false,
    segments: (d.segments || []).map((segment: HubChain["segments"][number]) => ({ ...segment, title: mazeTitle(segment.title) })),
    createdAt: date(d.createdAt),
    updatedAt: date(d.updatedAt),
    likeCount: d.likeCount || 0,
    commentCount: d.commentCount || 0,
    playCount: d.playCount || 0,
    isRemoved: d.isRemoved === true,
  };
}
const profileDoc = (id: string, d: DocumentData): HubProfile => ({
  id,
  displayName: d.displayName || "Builder",
  bio: d.bio || "",
  avatarUrl: d.avatarUrl,
  location: d.location || "",
  website: d.website || "",
  occupation: d.occupation || "",
  followerCount: Math.max(0, d.followerCount || 0),
  isSuspended: d.isSuspended === true,
  createdAt: date(d.createdAt),
  updatedAt: date(d.updatedAt),
});
export async function ensureProfile(u: HubUser) {
  if (!db) {
    const x = load();
    if (!x.profiles.some((p) => p.id === u.uid)) {
      x.profiles.push({
        id: u.uid,
        displayName: u.displayName,
        bio: "",
        location: "",
        website: "",
        occupation: "",
        followerCount: 0,
        createdAt: new Date(),
        updatedAt: new Date(),
      });
      save(x);
    }
    return;
  }
  const r = doc(db, C.users, u.uid),
    s = await getDoc(r);
  if (!s.exists())
    await setDoc(r, {
      displayName: u.displayName,
      bio: "",
      location: "",
      website: "",
      occupation: "",
      avatarUrl: u.photoURL || "",
      followerCount: 0,
      isSuspended: false,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
  else {
    const current = s.data(), patch: Record<string, unknown> = {};
    if (!current.displayName) patch.displayName = u.displayName || "Builder";
    if (current.bio === undefined) patch.bio = "";
    if (current.location === undefined) patch.location = "";
    if (current.website === undefined) patch.website = "";
    if (current.occupation === undefined) patch.occupation = "";
    if (!current.avatarUrl && u.photoURL) patch.avatarUrl = u.photoURL;
    if (Object.keys(patch).length)
      await setDoc(r, { ...patch, updatedAt: serverTimestamp() }, { merge: true });
  }
}
export async function profile(id: string): Promise<HubProfile | null> {
  if (!db) {
    const p = load().profiles.find((x) => x.id === id);
    return p
      ? {
          ...p,
          followerCount: p.followerCount || 0,
          createdAt: new Date(p.createdAt),
          updatedAt: new Date(p.updatedAt),
        }
      : {
          id,
          displayName: id === "local-builder" ? "Local Builder" : "Builder",
          bio: "",
          followerCount: 0,
          createdAt: new Date(),
          updatedAt: new Date(),
        };
  }
  const s = await getDoc(doc(db, C.users, id));
  return s.exists() ? profileDoc(s.id, s.data()) : null;
}
export async function profiles(
  includeSuspended = false,
): Promise<HubProfile[]> {
  if (!db)
    return load()
      .profiles.map((p) => ({
        ...p,
        followerCount: p.followerCount || 0,
        createdAt: new Date(p.createdAt),
        updatedAt: new Date(p.updatedAt),
      }))
      .filter((p) => includeSuspended || !p.isSuspended)
      .sort((a, b) => a.displayName.localeCompare(b.displayName));
  const s = await getDocs(collection(db, C.users));
  return s.docs
    .map((v) => profileDoc(v.id, v.data()))
    .filter((p) => includeSuspended || !p.isSuspended)
    .sort((a, b) => a.displayName.localeCompare(b.displayName));
}
export async function adminPosts(): Promise<
  Array<{
    type: "map" | "chain";
    id: string;
    title: string;
    authorName: string;
    authorId: string;
    isRemoved: boolean;
    createdAt: Date;
  }>
> {
  if (!db)
    return [
      ...load().maps.map((m) => ({
        type: "map" as const,
        id: m.id,
        title: m.title,
        authorName: m.authorName,
        authorId: m.authorId,
        isRemoved: m.isRemoved === true,
        createdAt: new Date(m.createdAt),
      })),
      ...load().chains.map((c) => ({
        type: "chain" as const,
        id: c.id,
        title: c.title,
        authorName: c.authorName,
        authorId: c.authorId,
        isRemoved: c.isRemoved === true,
        createdAt: new Date(c.createdAt),
      })),
    ].sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
  const [ms, cs] = await Promise.all([
    getDocs(collection(db, C.maps)),
    getDocs(collection(db, C.chains)),
  ]);
  return [
    ...ms.docs.map((v) => {
      const d = v.data();
      return {
        type: "map" as const,
        id: v.id,
        title: d.title || "Untitled maze",
        authorName: d.authorName || "Builder",
        authorId: d.authorId,
        isRemoved: d.isRemoved === true,
        createdAt: date(d.createdAt),
      };
    }),
    ...cs.docs.map((v) => {
      const d = v.data();
      return {
        type: "chain" as const,
        id: v.id,
        title: d.title || "Untitled joined maze",
        authorName: d.authorName || "Builder",
        authorId: d.authorId,
        isRemoved: d.isRemoved === true,
        createdAt: date(d.createdAt),
      };
    }),
  ].sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
}
export async function adminSetUserSuspended(id: string, isSuspended: boolean) {
  if (!db) {
    const x = load(),
      p = x.profiles.find((v) => v.id === id);
    if (p) {
      p.isSuspended = isSuspended;
      p.updatedAt = new Date();
      save(x);
    }
    return;
  }
  await updateDoc(doc(db, C.users, id), {
    isSuspended,
    updatedAt: serverTimestamp(),
  });
}
export async function adminSetPostRemoved(
  type: "map" | "chain",
  id: string,
  isRemoved: boolean,
) {
  if (!db) {
    const x = load(),
      list = type === "map" ? x.maps : x.chains,
      item = list.find((v) => v.id === id);
    if (item) {
      item.isRemoved = isRemoved;
      item.isPublic = !isRemoved;
      item.updatedAt = new Date();
      save(x);
    }
    return;
  }
  await content.moderatePost(type, id, isRemoved);
}
export async function saveProfile(
  u: HubUser,
  p: {
    displayName: string;
    bio: string;
    avatarUrl?: string;
    location?: string;
    website?: string;
    occupation?: string;
  },
) {
  if (!db) {
    const x = load(),
      i = x.profiles.findIndex((v) => v.id === u.uid),
      prior = i >= 0 ? x.profiles[i] : undefined,
      next: HubProfile = {
        id: u.uid,
        displayName: p.displayName,
        bio: p.bio,
        avatarUrl: p.avatarUrl ?? prior?.avatarUrl ?? "",
        location: p.location || "",
        website: p.website || "",
        occupation: p.occupation || "",
        followerCount: prior?.followerCount || 0,
        createdAt: prior ? new Date(prior.createdAt) : new Date(),
        updatedAt: new Date(),
      };
    if (i >= 0) x.profiles[i] = next;
    else x.profiles.push(next);
    save(x);
    return;
  }
  await setDoc(
    doc(db, C.users, u.uid),
    { ...p, updatedAt: serverTimestamp() },
    { merge: true },
  );
}
export async function uploadProfileAvatar(u: HubUser, blob: Blob) {
  if (!storage) {
    return await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result || ""));
      reader.onerror = () => reject(new Error("Could not read the profile image."));
      reader.readAsDataURL(blob);
    });
  }
  const avatarRef = ref(storage, `reactive-worlds/profiles/${u.uid}/avatar.png`);
  await uploadBytes(avatarRef, blob, {
    contentType: "image/png",
    cacheControl: "public,max-age=300",
  });
  const url = await getDownloadURL(avatarRef);
  return `${url}${url.includes("?") ? "&" : "?"}v=${Date.now()}`;
}
export async function isFollowing(userId: string, targetId: string) {
  if (!db) return false;
  return (
    await getDoc(doc(db, C.users, userId, "following", targetId))
  ).exists();
}
export async function toggleFollowing(u: HubUser, targetId: string) {
  if (!db || u.uid === targetId) return false;
  return (await community.toggleFollowing(targetId)).following;
}
export async function following(userId: string): Promise<HubProfile[]> {
  if (!db) return [];
  const s = await getDocs(collection(db, C.users, userId, "following")),
    items = await Promise.all(s.docs.map((v) => profile(v.id)));
  return items.filter((v): v is HubProfile => !!v);
}
export async function maps(
  o: { publicOnly?: boolean; authorId?: string } = {},
): Promise<HubMap[]> {
  if (!db)
    return load()
      .maps.map(reviveMap)
      .filter(
        (m) =>
          !m.isRemoved &&
          (!o.publicOnly || m.isPublic) &&
          (!o.authorId || m.authorId === o.authorId),
      )
      .sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
  let q = query(collection(db, C.maps));
  if (o.authorId)
    q = query(collection(db, C.maps), where("authorId", "==", o.authorId));
  else if (o.publicOnly)
    q = query(collection(db, C.maps), where("isPublic", "==", true), where("isRemoved", "==", false));
  const s = await getDocs(q);
  return s.docs
    .map((v) => mapDoc(v.id, v.data()))
    .filter((m) => !m.isRemoved)
    .sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
}
export async function chains(
  o: { publicOnly?: boolean; authorId?: string } = {},
): Promise<HubChain[]> {
  if (!db)
    return load()
      .chains.map(reviveChain)
      .filter(
        (c) =>
          !c.isRemoved &&
          (!o.publicOnly || c.isPublic) &&
          (!o.authorId || c.authorId === o.authorId),
      )
      .sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
  let q = query(collection(db, C.chains));
  if (o.authorId)
    q = query(collection(db, C.chains), where("authorId", "==", o.authorId));
  else if (o.publicOnly)
    q = query(collection(db, C.chains), where("isPublic", "==", true), where("isRemoved", "==", false));
  const s = await getDocs(q);
  return s.docs
    .map((v) => chainDoc(v.id, v.data()))
    .filter((c) => !c.isRemoved)
    .sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
}
export async function feed(): Promise<FeedItem[]> {
  const [m, c] = await Promise.all([
    maps({ publicOnly: true }),
    chains({ publicOnly: true }),
  ]);
  return [
    ...m.map((data) => ({
      type: "map" as const,
      id: data.id,
      data,
      createdAt: data.createdAt,
    })),
    ...c.map((data) => ({
      type: "chain" as const,
      id: data.id,
      data,
      createdAt: data.createdAt,
    })),
  ].sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
}
export async function map(id: string): Promise<HubMap | null> {
  if (!db) {
    const m = load().maps.find((v) => v.id === id);
    return m ? reviveMap(m) : null;
  }
  const s = await getDoc(doc(db, C.maps, id));
  return s.exists() ? mapDoc(s.id, s.data()) : null;
}
export async function chain(id: string): Promise<HubChain | null> {
  if (!db) {
    const c = load().chains.find((v) => v.id === id);
    return c ? reviveChain(c) : null;
  }
  const s = await getDoc(doc(db, C.chains, id));
  return s.exists() ? chainDoc(s.id, s.data()) : null;
}
export async function mapPackage(m: HubMap): Promise<ReactiveWorldsMapPackage> {
  let pkg: ReactiveWorldsMapPackage;
  if (m.package) pkg = structuredClone(m.package);
  else {
    if (!m.packageUrl) throw new Error("Maze data is missing.");
    const r = await fetch(m.packageUrl);
    if (!r.ok) throw new Error("Could not download maze data.");
    pkg = await r.json() as ReactiveWorldsMapPackage;
  }
  return {
    ...pkg,
    ugc: {
      ...pkg.ugc,
      creatorName: m.authorName,
      bountyGold: m.bountyGold,
    },
  };
}
export async function publishMap(
  u: HubUser,
  p: ReactiveWorldsMapPackage,
  meta: {
    title: string;
    description: string;
    tags: string[];
    isPublic: boolean;
    verification: ProjectVerification;
  },
) {
  const revisionHash = await mapRevisionHash(p);
  if (!meta.verification || meta.verification.revisionHash !== revisionHash)
    throw new Error("This exact gameplay revision has not been creator-completed.");
  const id = uid("map"),
    packageJson = JSON.stringify(p),
    portablePackage = JSON.parse(packageJson) as ReactiveWorldsMapPackage,
    base = {
      authorId: u.uid,
      authorName: u.displayName,
      title: meta.title.trim() || titleOf(p),
      description: meta.description,
      theme: p.theme,
      levelType: String(p.ugc.levelType || "Puzzle"),
      physicsVersion: p.engineVersion || p.version,
      isPublic: meta.isPublic,
      tags: meta.tags,
      likeCount: 0,
      ratingSum: 0,
      ratingCount: 0,
      commentCount: 0,
      playCount: 0,
      remixCount: 0,
      remixOf: p.hub?.remixOf || null,
      rootMapId: p.hub?.rootMapId || null,
      lineage: p.hub?.lineage || [],
      verified: true,
      revisionHash,
      creatorVerifiedAt: meta.verification.verifiedAt,
      uniqueAttemptCount: 0,
      uniqueFailureCount: 0,
      escapeCount: 0,
      abandonCount: 0,
      rematchCount: 0,
      bountyGold: 25,
      creatorEarningsGold: 0,
      lineageRoyaltyGold: 0,
      saveCount: 0,
    };
  const thumb = await runtimePreviewBlob(p);
  if (!db) {
    const thumbnailUrl = await new Promise<string>((resolve, reject) => {
      const r = new FileReader();
      r.onload = () => resolve(String(r.result));
      r.onerror = () => reject(r.error);
      r.readAsDataURL(thumb);
    });
    const x = load();
    x.maps.push({
      id,
      ...base,
      averageRating: 0,
      package: portablePackage,
      thumbnailUrl,
      createdAt: new Date(),
      updatedAt: new Date(),
      remixOf: p.hub?.remixOf,
      rootMapId: p.hub?.rootMapId,
    });
    if (p.hub?.remixOf) {
      const s = x.maps.find((v) => v.id === p.hub?.remixOf);
      if (s) s.remixCount++;
    }
    save(x);
    return id;
  }
  if (!meta.verification.verificationId || meta.verification.authority !== "server")
    throw new Error("This completion has not been attested by the server.");
  const thumbnailDataUrl = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(thumb);
  });
  await publishVerifiedMapDocument({
    mapId: id,
    verificationId: meta.verification.verificationId,
    packageJson,
    thumbnailDataUrl,
    authorName: u.displayName,
    title: base.title,
    description: base.description,
    tags: base.tags,
    isPublic: base.isPublic,
  });
  return id;
}
export async function createChain(
  u: HubUser,
  v: {
    title: string;
    description: string;
    isPublic: boolean;
    segments: ChainSegment[];
  },
) {
  const id = uid("chain");
  if (!db) {
    const x = load();
    x.chains.push({
      id,
      authorId: u.uid,
      authorName: u.displayName,
      ...v,
      createdAt: new Date(),
      updatedAt: new Date(),
      likeCount: 0,
      commentCount: 0,
      playCount: 0,
    });
    save(x);
    return id;
  }
  await setDoc(doc(db, C.chains, id), {
    authorId: u.uid,
    authorName: u.displayName,
    ...v,
    likeCount: 0,
    commentCount: 0,
    playCount: 0,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return id;
}
export async function liked(userId: string, t: TargetType, id: string) {
  const k = `${t}_${id}_${userId}`;
  if (!db) return load().likes.includes(k);
  return (await getDoc(doc(db, C.likes, k))).exists();
}
export async function mapRating(userId: string, mapId: string) {
  const key = `${mapId}_${userId}`;
  if (!db) return load().ratings[key] ?? null;
  const snapshot = await getDoc(doc(db, C.maps, mapId, "ratings", userId));
  return snapshot.exists() ? Number(snapshot.data().rating) || null : null;
}
export async function rateMap(u: HubUser, mapId: string, value: number) {
  const rating = Math.round(Number(value));
  if (rating < 1 || rating > 5) throw new Error("Rating must be from 1 to 5 stars.");
  if (!db) {
    const x = load(),
      item = x.maps.find((map) => map.id === mapId),
      key = `${mapId}_${u.uid}`;
    if (!item) throw new Error("Maze not found.");
    if (item.authorId === u.uid)
      throw new Error("Creators cannot rate their own mazes.");
    const previous = x.ratings[key];
    item.ratingSum = Math.max(0, Number(item.ratingSum) || 0) - (previous || 0) + rating;
    item.ratingCount = Math.max(0, Number(item.ratingCount) || 0) + (previous ? 0 : 1);
    item.averageRating = item.ratingCount ? item.ratingSum / item.ratingCount : 0;
    x.ratings[key] = rating;
    save(x);
    return { userRating: rating, ratingSum: item.ratingSum, ratingCount: item.ratingCount, averageRating: item.averageRating };
  }
  return community.rateMap(mapId, rating, u.displayName);
}
export async function toggleLike(u: HubUser, t: TargetType, id: string) {
  const k = `${t}_${id}_${u.uid}`;
  if (!db) {
    const x = load(),
      i = x.likes.indexOf(k),
      yes = i < 0;
    if (yes) x.likes.push(k);
    else x.likes.splice(i, 1);
    const list = t === "map" ? x.maps : x.chains,
      item = list.find((v) => v.id === id);
    if (item) item.likeCount = Math.max(0, item.likeCount + (yes ? 1 : -1));
    save(x);
    return yes;
  }
  return (await community.toggleLike(t, id)).liked;
}
export async function comments(
  t: TargetType,
  id: string,
): Promise<HubComment[]> {
  if (!db)
    return load()
      .comments.map(reviveComment)
      .filter((c) => c.targetType === t && c.targetId === id)
      .sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime());
  const s = await getDocs(
    query(collection(db, C.comments), where("targetType", "==", t), where("targetId", "==", id)),
  );
  return s.docs
    .map((v) => {
      const d = v.data();
      return {
        id: v.id,
        targetType: d.targetType,
        targetId: d.targetId,
        userId: d.userId,
        userName: d.userName,
        content: d.content,
        createdAt: date(d.createdAt),
        updatedAt: date(d.updatedAt),
      } as HubComment;
    })
    .filter((c) => c.targetType === t)
    .sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime());
}
export async function addComment(
  u: HubUser,
  t: TargetType,
  id: string,
  content: string,
) {
  const c: HubComment = {
    id: uid("comment"),
    targetType: t,
    targetId: id,
    userId: u.uid,
    userName: u.displayName,
    content: content.trim(),
    createdAt: new Date(),
    updatedAt: new Date(),
  };
  if (!db) {
    const x = load();
    x.comments.push(c);
    const list = t === "map" ? x.maps : x.chains,
      item = list.find((v) => v.id === id);
    if (item) item.commentCount++;
    save(x);
    return c;
  }
  const result = await community.addComment(t, id, c.content, u.displayName);
  return { ...c, id: result.commentId };
}
export async function play(t: TargetType, id: string) {
  if (!db) {
    const x = load(),
      list = t === "map" ? x.maps : x.chains,
      item = list.find((v) => v.id === id);
    if (item) item.playCount++;
    save(x);
    return;
  }
  await community.recordCommunityPlay(t, id);
}
